import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { StatCard } from '../primitives';
import { apiFetch } from '../../lib/api';
import { ACCENTS } from '../../lib/theme';
import type { Job, JobDetail, MaterialLine } from '../../hooks/useJobs';

/**
 * Profit & Loss — the legacy tab (jobs_detail_view.php "PROFIT & LOSS"): six
 * figures, Add Material, and one table of the job's timesheet lines and
 * material lines together. Materials are edited and deleted here; labour lines
 * belong to the Timesheet module and are read-only.
 */

interface ProfitLossTabProps {
    readonly detail: JobDetail;
    readonly value: (key: keyof Job) => string;
    readonly set: (key: keyof Job) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    /** Reload the job after a material change. Does not close the dialog. */
    readonly onChanged: () => void;
}

interface Line {
    readonly key: string;
    readonly date: string;
    readonly description: string;
    readonly staffName: string;
    readonly ratePerHour: number | null;
    readonly hours: number | null;
    readonly costLabour: number | null;
    readonly costMaterial: number | null;
    readonly material: MaterialLine | null;
}

function money(v: number | null): string {
    if (v === null) {
        return '';
    }
    const abs = Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${v < 0 ? '-' : ''}$${abs}`;
}

function formatDate(iso: string): string {
    const d = iso.slice(0, 10).split('-');
    return d.length === 3 ? `${d[2] ?? ''}-${d[1] ?? ''}-${d[0] ?? ''}` : iso;
}

export function ProfitLossTab({ detail, value, set, onChanged }: ProfitLossTabProps): React.JSX.Element {
    const pl = detail.profitLoss;
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState<MaterialLine | null>(null);
    const [description, setDescription] = useState('');
    const [cost, setCost] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const lines = useMemo<readonly Line[]>(() => {
        const all: Line[] = [
            ...pl.materialLines.map((m) => ({
                key: `m${String(m.id)}`,
                date: m.createdAt.slice(0, 10),
                description: m.description,
                staffName: m.addedBy ?? '',
                ratePerHour: null,
                hours: null,
                costLabour: null,
                costMaterial: m.cost,
                material: m,
            })),
            ...pl.labourLines.map((l) => ({
                key: `l${String(l.id)}`,
                date: l.workDate,
                description: '',
                staffName: l.staffName ?? '',
                ratePerHour: l.ratePerHour,
                hours: l.hours,
                costLabour: l.costLabour,
                costMaterial: null,
                material: null,
            })),
        ];
        return all.sort((a, b) => b.date.localeCompare(a.date));
    }, [pl.materialLines, pl.labourLines]);

    const lastPage = Math.max(0, Math.ceil(lines.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = lines.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    function openForm(m: MaterialLine | null): void {
        setEditing(m);
        setAdding(m === null);
        setDescription(m?.description ?? '');
        setCost(m === null ? '' : String(m.cost));
        setError(null);
    }

    function closeForm(): void {
        setAdding(false);
        setEditing(null);
        setError(null);
    }

    function saveMaterial(): void {
        const amount = Number(cost.replace(/[$,\s]/g, ''));
        if (description.trim() === '' || cost.trim() === '' || !Number.isFinite(amount) || amount < 0) {
            setError('Enter a description and a cost of 0 or more');
            return;
        }
        setBusy(true);
        setError(null);
        const base = `/jobs/${String(detail.job.id)}/materials`;
        apiFetch<MaterialLine>(editing === null ? base : `${base}/${String(editing.id)}`, {
            method: editing === null ? 'POST' : 'PATCH',
            body: JSON.stringify({ description: description.trim(), cost: amount }),
        })
            .then(() => {
                closeForm();
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save the material');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function deleteMaterial(id: number): void {
        setBusy(true);
        setError(null);
        apiFetch<unknown>(`/jobs/${String(detail.job.id)}/materials/${String(id)}`, { method: 'DELETE' })
            .then(() => {
                setConfirmDelete(null);
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not delete the material');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const profit = pl.profitLoss ?? 0;
    const formOpen = adding || editing !== null;

    return (
        <Stack gap={2.5}>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
                }}
            >
                <StatCard
                    title="Budget"
                    icon={<Box />}
                    colour={ACCENTS.green}
                    metrics={[{ label: 'Budget', value: money(pl.budgetCost ?? 0) }]}
                />
                <StatCard
                    title="Total Hours"
                    icon={<Box />}
                    colour={ACCENTS.pink}
                    metrics={[{ label: 'Worked', value: pl.totalHours.toFixed(2) }]}
                />
                <StatCard
                    title="Materials"
                    icon={<Box />}
                    colour={ACCENTS.amber}
                    metrics={[{ label: 'Cost', value: money(pl.costMaterials ?? 0) }]}
                />
                <StatCard
                    title="Labour"
                    icon={<Box />}
                    colour={ACCENTS.teal}
                    metrics={[{ label: 'Cost', value: money(pl.costLabour) }]}
                />
                <StatCard
                    title="Total"
                    icon={<Box />}
                    colour={ACCENTS.purple}
                    metrics={[{ label: 'Labour + materials', value: money(pl.totalCost) }]}
                />
                <StatCard
                    title="Profit / Loss"
                    icon={<Box />}
                    colour={profit < 0 ? ACCENTS.red : ACCENTS.green}
                    metrics={[{ label: profit < 0 ? 'Loss' : 'Profit', value: money(profit) }]}
                />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                    label="Budget"
                    size="small"
                    value={value('budgetCost')}
                    onChange={set('budgetCost')}
                    helperText="Saved with Update Job"
                    slotProps={{ input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> } }}
                    sx={{ width: { sm: 220 } }}
                />
                <Box sx={{ flex: 1 }} />
                {!formOpen ? (
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            openForm(null);
                        }}
                    >
                        Add Material
                    </Button>
                ) : null}
            </Stack>

            {formOpen ? (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        {editing === null ? 'Add Material' : 'Update Material'}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'flex-start' }}>
                        <TextField
                            label="Description"
                            size="small"
                            fullWidth
                            autoFocus
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                            }}
                        />
                        <TextField
                            label="Cost"
                            size="small"
                            value={cost}
                            onChange={(e) => {
                                setCost(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    saveMaterial();
                                }
                            }}
                            slotProps={{
                                input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> },
                                htmlInput: { inputMode: 'decimal' },
                            }}
                            sx={{ width: { sm: 200 } }}
                        />
                        <Button onClick={closeForm} disabled={busy}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={saveMaterial} disabled={busy}>
                            {editing === null ? 'Save' : 'Update'}
                        </Button>
                    </Stack>
                </Box>
            ) : null}

            {error !== null ? <Alert severity="error">{error}</Alert> : null}

            <Box>
                <TableContainer
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: 860 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Staff Name</TableCell>
                                <TableCell align="right">Rate Per Hour</TableCell>
                                <TableCell align="right">Hours</TableCell>
                                <TableCell align="right">Cost Labour</TableCell>
                                <TableCell align="right">Cost Material</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.secondary">
                                            No timesheet hours or materials on this job yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pageRows.map((l, idx) => (
                                    <TableRow
                                        key={l.key}
                                        hover
                                        sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                    >
                                        <TableCell>{formatDate(l.date)}</TableCell>
                                        <TableCell>{l.description}</TableCell>
                                        <TableCell>{l.staffName}</TableCell>
                                        <TableCell align="right">{money(l.ratePerHour)}</TableCell>
                                        <TableCell align="right">{l.hours ?? ''}</TableCell>
                                        <TableCell align="right">{money(l.costLabour)}</TableCell>
                                        <TableCell align="right">{money(l.costMaterial)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {l.material !== null && confirmDelete === l.material.id ? (
                                                <Stack direction="row" gap={0.5} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="contained"
                                                        disabled={busy}
                                                        onClick={() => {
                                                            if (l.material !== null) {
                                                                deleteMaterial(l.material.id);
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setConfirmDelete(null);
                                                        }}
                                                    >
                                                        Keep
                                                    </Button>
                                                </Stack>
                                            ) : l.material !== null ? (
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => {
                                                                openForm(l.material);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                if (l.material !== null) {
                                                                    setConfirmDelete(l.material.id);
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={lines.length}
                    page={currentPage}
                    onPageChange={(_e, p) => {
                        setPage(p);
                    }}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(Number.parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Box>
        </Stack>
    );
}
